// Tenant-timezone-aware calendar-date utilities for the booking flow.
//
// Why this exists: bare `new Date()` + `.toISOString().split('T')[0]`
// patterns silently corrupt dates in negative-UTC-offset zones during
// evening hours. A customer in EDT picks "May 10" from the calendar at
// 10pm local; .toISOString() returns 2026-05-11T02:00:00Z; .split('T')[0]
// yields "2026-05-11"; the booking is stored a day off. Likewise, a
// Railway container running in UTC mis-judges "today is May 10 EDT" as
// "today is May 11 UTC" — its `Date` reads May 11 starting at 8pm EDT.
//
// Fix: route every calendar-date read/write through `Intl.DateTimeFormat`
// with an explicit IANA timezone (the tenant's configured value from
// business_settings.timezone). Host TZ is then irrelevant.

const DEFAULT_TZ = 'America/Indiana/Indianapolis';

// Format a JS Date as a YYYY-MM-DD string in the given timezone.
// 'en-CA' locale outputs ISO-style YYYY-MM-DD with dash separators.
export function formatYmdInTimezone(date, timezone) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || DEFAULT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

// Today's calendar date as YYYY-MM-DD in the given timezone.
export function getTodayYmd(timezone) {
  return formatYmdInTimezone(new Date(), timezone);
}

// Whole-day difference between a YYYY-MM-DD string and today (in tz).
// Returns NaN if the input doesn't parse. Uses UTC epoch math on the
// parsed parts so host TZ can't leak in.
export function daysFromToday(input, timezone) {
  if (input == null || input === '') return NaN;
  // Accept three shapes:
  //   1. YYYY-MM-DD strings (booking form / API request body)
  //   2. ISO timestamps from JSON (YYYY-MM-DDTHH:MM:SSZ — slice keeps the date)
  //   3. Date objects (e.g. when the BookingForm hands the helper a
  //      JS Date directly).
  let dateStr;
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return NaN;
    // toISOString() uses UTC. For a Date constructed from a calendar
    // date string, the ISO slice recovers the original calendar date
    // regardless of host TZ.
    dateStr = input.toISOString().slice(0, 10);
  } else {
    dateStr = String(input).slice(0, 10);
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return NaN;
  const today = getTodayYmd(timezone);
  const tm = /^(\d{4})-(\d{2})-(\d{2})/.exec(today);
  if (!tm) return NaN;
  const todayUTC = Date.UTC(Number(tm[1]), Number(tm[2]) - 1, Number(tm[3]));
  const targetUTC = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round((targetUTC - todayUTC) / 86400000);
}
